import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const CheckIcon = ({ size = 10, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 10 8" fill="none" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0, ...style }}>
    <path d="M1 4L3.8 7L9 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function ProjectMultiSelect({ value = [], options, onChange, disabled, isDirty }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top:0, left:0, width:0 });
  const btnRef   = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  function toggle() {
    if (disabled) return;
    if (!open) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) });
    }
    setOpen(o => !o);
  }

  function toggleProject(id) {
    if (value.includes(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  }

  let label;
  if (value.length === 0) label = '— none —';
  else if (value.length === 1) label = options.find(o => o.id === value[0])?.name ?? '1 project';
  else label = `${value.length} projects`;

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={disabled}
        style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:6,
          fontFamily:'var(--font-sans)', fontSize:12,
          padding:'5px 10px', borderRadius:'var(--radius-md)',
          border:`1px solid ${isDirty ? 'var(--color-amber)' : 'var(--color-border2)'}`,
          background:'var(--color-card2)',
          color: isDirty ? 'var(--color-amber)' : 'var(--color-text)',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          minWidth:130, maxWidth:190, outline:'none',
          transition:'border-color 0.15s, box-shadow 0.15s',
          boxShadow: open ? '0 0 0 2px var(--color-accent-border)' : 'none',
        }}
      >
        <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, textAlign:'left'}}>
          {label}
        </span>
        <span style={{transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.15s', color:'var(--color-muted2)', display:'flex'}}>
          <ChevronDown />
        </span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position:'fixed', top:pos.top, left:pos.left, minWidth:pos.width,
            background:'rgba(255,255,255,0.95)',
            backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
            border:'1px solid rgba(199,210,254,0.6)', borderRadius:'var(--radius-md)',
            boxShadow:'0 8px 32px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
            zIndex:9999, overflow:'hidden', maxHeight:220, overflowY:'auto',
          }}
        >
          <div
            onClick={() => onChange([])}
            style={{
              padding:'7px 12px', fontSize:11, cursor:'pointer',
              color: value.length === 0 ? '#94A3B8' : '#64748B',
              borderBottom:'1px solid rgba(199,210,254,0.4)',
              background: value.length === 0 ? '#F8FAFC' : 'transparent',
              fontStyle:'italic',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; }}
            onMouseLeave={e => { e.currentTarget.style.background = value.length === 0 ? '#F8FAFC' : 'transparent'; }}
          >
            — none —
          </div>
          {options.map(opt => {
            const checked = value.includes(opt.id);
            return (
              <div
                key={opt.id}
                onClick={() => toggleProject(opt.id)}
                style={{
                  padding:'8px 12px', fontSize:12, cursor:'pointer',
                  color: checked ? '#4338CA' : '#0F172A',
                  background: checked ? '#EEF2FF' : 'transparent',
                  display:'flex', alignItems:'center', gap:9,
                  transition:'background 0.1s',
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#F1F5F9'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width:15, height:15, borderRadius:4, flexShrink:0,
                  border:`1.5px solid ${checked ? '#4338CA' : '#CBD5E1'}`,
                  background: checked ? '#4338CA' : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'background 0.1s, border-color 0.1s',
                }}>
                  {checked && (
                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                      <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{opt.name}</span>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';
const STATUS_COLOR = { ready: 'var(--color-green)', processing: 'var(--color-amber)', failed: 'var(--color-danger)', queued: 'var(--color-muted)', uploading: 'var(--color-accent)' };
const STATUS_BG    = { ready: 'rgba(42,139,122,0.12)', processing: 'rgba(245,169,127,0.12)', failed: 'rgba(229,62,62,0.10)', queued: 'rgba(0,0,0,0.05)', uploading: 'rgba(0,255,255,0.08)' };
const ACCEPTED = '.mp3,.mp4,.wav,.m4a,.txt,.pdf,.docx';
const POLL_INTERVAL = 5000;
const MAX_POLLS = 72;

export default function ProjectDetail() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [project, setProject]   = useState(null);
  const [sessions, setSessions] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending]   = useState({});
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const pollsRef  = useRef({});
  const fileInput = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [projRes, sessRes, allProjRes] = await Promise.all([
        fetch(`${API_BASE}/v1/projects`, { headers }),
        fetch(`${API_BASE}/v1/sessions?limit=100&project_id=${projectId}`, { headers }),
        fetch(`${API_BASE}/v1/projects`, { headers }),
      ]);
      if (projRes.ok) {
        const list = await projRes.json();
        const found = list.find(p => p.id === projectId);
        if (!found) { navigate('/projects'); return; }
        setProject(found);
      }
      if (sessRes.ok) {
        const d = await sessRes.json();
        const items = d.sessions || [];
        setSessions(items);
        items.forEach(s => {
          if (s.status === 'queued' || s.status === 'processing') startPolling(s.id);
        });
      }
      if (allProjRes.ok) setAllProjects(await allProjRes.json());
    } catch {
      setError('Could not load project.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, projectId]);

  useEffect(() => {
    loadData();
    return () => Object.values(pollsRef.current).forEach(p => clearInterval(p.interval));
  }, [loadData]);

  function startPolling(sessionId) {
    if (pollsRef.current[sessionId]) return;
    let count = 0;
    const interval = setInterval(async () => {
      count++;
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/v1/sessions/${sessionId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { status, progress_pct } = await res.json();
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, status, progress_pct } : s));
        if (status === 'ready' || status === 'failed' || count >= MAX_POLLS) {
          clearInterval(pollsRef.current[sessionId]?.interval);
          delete pollsRef.current[sessionId];
        }
      } catch { /* keep polling */ }
    }, POLL_INTERVAL);
    pollsRef.current[sessionId] = { interval, count };
  }

  async function deleteSession(id) {
    if (!window.confirm('Delete this session permanently? This cannot be undone.')) return;
    if (pollsRef.current[id]) {
      clearInterval(pollsRef.current[id].interval);
      delete pollsRef.current[id];
    }
    const token = await getToken();
    await fetch(`${API_BASE}/v1/sessions/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setSessions(prev => prev.filter(s => s.id !== id));
  }

  async function uploadFiles(files) {
    const fileList = Array.from(files);
    if (!fileList.length) return;
    const token = await getToken();

    const placeholders = fileList.map(f => ({
      id: `uploading-${f.name}-${Date.now()}`,
      name: f.name.replace(/\.[^/.]+$/, ''),
      status: 'uploading',
      quote_count: 0,
      created_at: new Date().toISOString(),
      project: projectId,
      _placeholder: true,
    }));
    setSessions(prev => [...placeholders, ...prev]);

    await Promise.all(fileList.map(async (file, i) => {
      const placeholderId = placeholders[i].id;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_name', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('project_id', projectId);
      try {
        const res = await fetch(`${API_BASE}/v1/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { session_id } = await res.json();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'session_upload', file_type: file.name.split('.').pop().toLowerCase() });
        setSessions(prev => prev.map(s => s.id === placeholderId ? {
          ...s, id: session_id, status: 'queued', _placeholder: false,
        } : s));
        startPolling(session_id);
      } catch {
        setSessions(prev => prev.map(s => s.id === placeholderId ? { ...s, status: 'failed' } : s));
      }
    }));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  }

  function handleFileInput(e) {
    uploadFiles(e.target.files);
    e.target.value = '';
  }

  function handleProjectChange(sessionId, newProjectIds) {
    const original = JSON.stringify([...(sessions.find(s => s.id === sessionId)?.projects || [projectId])].sort());
    const updated  = JSON.stringify([...newProjectIds].sort());
    if (original === updated) {
      setPending(prev => { const n = { ...prev }; delete n[sessionId]; return n; });
    } else {
      setPending(prev => ({ ...prev, [sessionId]: newProjectIds }));
    }
  }

  const dirtyCount = Object.keys(pending).length;

  async function saveChanges() {
    if (!dirtyCount) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const results = await Promise.all(
        Object.entries(pending).map(([sessionId, newProjectIds]) =>
          fetch(`${API_BASE}/v1/sessions/${sessionId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_ids: newProjectIds }),
          })
        )
      );
      if (!results.every(r => r.ok)) throw new Error('Some updates failed');
      // Remove sessions no longer belonging to this project
      setSessions(prev => prev
        .map(s => pending.hasOwnProperty(s.id) ? { ...s, projects: pending[s.id], project: pending[s.id][0] || null } : s)
        .filter(s => !pending.hasOwnProperty(s.id) || pending[s.id].includes(projectId))
      );
      setPending({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Failed to save some changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={s.center}>Loading project...</div>;
  if (!project) return null;

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      {dragging && (
        <div style={s.dragOverlay}>
          <div style={s.dragMsg}>Drop files — they'll be assigned to {project.name}</div>
        </div>
      )}

      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <button style={s.backBtn} onClick={() => navigate('/projects')} title="Back to projects">
            ← Back
          </button>
          <div>
            <div style={s.projectBadge}>Project</div>
            <h1 style={s.h1}>{project.name}</h1>
            {project.description && <p style={s.subtitle}>{project.description}</p>}
          </div>
        </div>
        <div style={s.headerActions}>
          {saved && <span style={s.savedBadge}><CheckIcon size={11} style={{marginRight:5}} /> Saved</span>}
          {dirtyCount > 0 && (
            <>
              <button style={s.discardBtn} onClick={() => setPending({})} disabled={saving}>Discard</button>
              <button style={s.saveBtn} onClick={saveChanges} disabled={saving}>
                {saving ? 'Saving…' : `Save ${dirtyCount} change${dirtyCount !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          <button
            style={s.chatBtn}
            onClick={() => navigate('/chat', { state: { projectId, projectName: project.name } })}
          >
            ✦ Research chat
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          <button style={s.uploadBtn} onClick={() => fileInput.current.click()}>
            ⬆ Upload to this project
          </button>
        </div>
      </div>

      {error && <p style={s.errorMsg}>{error}</p>}

      {/* Empty state / drop zone */}
      {sessions.length === 0 && (
        <div style={s.dropZone} onClick={() => fileInput.current.click()}>
          <div style={s.dropIcon}>⬆</div>
          <div style={s.dropTitle}>Drop files here or click to upload</div>
          <div style={s.dropSub}>
            Sessions will be automatically assigned to <strong style={{ color: 'var(--color-text)' }}>{project.name}</strong>
          </div>
          <div style={{ ...s.dropSub, marginTop: 6 }}>mp3, mp4, wav, m4a, txt, pdf, docx</div>
        </div>
      )}

      {/* Sessions table */}
      {sessions.length > 0 && (
        <div className="scq-card" style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Name', 'Status', 'Progress', 'Project', 'Quotes', 'Date', ''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(sess => {
                const isUploading = sess.status === 'uploading';
                const isLive = sess.status === 'queued' || sess.status === 'processing';
                const currentProjects = pending.hasOwnProperty(sess.id) ? pending[sess.id] : (sess.projects || [projectId]);
                const isDirty = pending.hasOwnProperty(sess.id);
                return (
                  <tr key={sess.id} style={{ ...s.tr, ...(isDirty ? s.trDirty : {}) }}>
                    <td style={s.td}>
                      <div style={s.sessionName}>{sess.name}</div>
                      {!sess._placeholder && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                          <div style={s.sessionId}>{sess.id.slice(0, 8)}…</div>
                          {sess.status === 'ready' && (
                            <button
                              style={s.sessionChatBtn}
                              onClick={() => navigate('/chat', { state: { sessionId: sess.id, sessionName: sess.name } })}
                            >
                              ✦ Chat
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, background: STATUS_BG[sess.status] || STATUS_BG.queued, color: STATUS_COLOR[sess.status] || 'var(--color-muted)' }}>
                        {isLive ? <span style={s.pulse} /> : null}
                        {sess.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      {(isUploading || isLive) ? (
                        <div style={s.progressTrack}>
                          <div style={{ ...s.progressFill, width: isUploading ? '15%' : sess.status === 'processing' ? '60%' : '10%' }} />
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: sess.status === 'ready' ? 'var(--color-green)' : 'var(--color-muted2)' }}>
                          {sess.status === 'ready' ? <><CheckIcon size={10} style={{marginRight:4}} />Done</> : sess.status === 'failed' ? '✕ Failed' : '—'}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      <ProjectMultiSelect
                        value={currentProjects}
                        options={allProjects}
                        onChange={val => handleProjectChange(sess.id, val)}
                        disabled={isUploading || sess._placeholder}
                        isDirty={isDirty}
                      />
                    </td>
                    <td style={s.td}>{sess.quote_count || 0}</td>
                    <td style={s.td}>{sess.created_at ? new Date(sess.created_at).toLocaleDateString() : '—'}</td>
                    <td style={{...s.td, textAlign:'right'}}>
                      {!sess._placeholder && (
                        <button style={s.deleteBtn} onClick={() => deleteSession(sess.id)} title="Delete session">✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={s.tableDropHint} onClick={() => fileInput.current.click()}>
            ⬆ Drop more files here or click to add sessions to {project.name}
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      {dirtyCount > 0 && (
        <div style={s.stickyBar}>
          <span style={s.stickyMsg}>{dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.discardBtn} onClick={() => setPending({})} disabled={saving}>Discard</button>
            <button style={s.saveBtn} onClick={saveChanges} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scqPulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes progressShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

const s = {
  center:      { color: 'var(--color-muted)', textAlign: 'center', padding: '80px 0', fontSize: 14 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  backBtn:     { padding: '7px 14px', background: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 13, cursor: 'pointer', marginTop: 4, flexShrink: 0 },
  projectBadge:{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-muted2)', marginBottom: 4 },
  h1:          { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--color-text)', margin: 0 },
  subtitle:    { color: 'var(--color-muted)', marginTop: 6, fontSize: 14, maxWidth: 500 },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  uploadBtn:   { padding: '10px 18px', background: 'var(--color-magma)', color: '#fff', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  chatBtn:     { padding: '10px 16px', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', border: '1px solid var(--color-accent-border)', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  sessionChatBtn: { padding: '2px 8px', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', border: '1px solid var(--color-accent-border)', borderRadius: 'var(--radius-sm)', fontSize: 10, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.01em' },
  saveBtn:     { padding: '10px 20px', background: 'var(--color-card2)', color: 'var(--color-accent)', border: '1px solid var(--color-border2)', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  discardBtn:  { padding: '10px 16px', background: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', fontSize: 13, cursor: 'pointer' },
  savedBadge:  { fontSize: 13, color: 'var(--color-green)', fontWeight: 600 },
  errorMsg:    { color: 'var(--color-danger)', fontSize: 13, marginBottom: 16 },

  dropZone:    { border: '2px dashed var(--color-border2)', borderRadius: 'var(--radius-xl)', padding: '60px 24px', textAlign: 'center', cursor: 'pointer', marginBottom: 24 },
  dropIcon:    { fontSize: 32, marginBottom: 12, color: 'var(--color-muted2)' },
  dropTitle:   { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 },
  dropSub:     { fontSize: 13, color: 'var(--color-muted2)' },

  dragOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,255,255,0.06)', border: '3px dashed var(--color-accent)', borderRadius: 'var(--radius-xl)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' },
  dragMsg:     { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--color-accent)' },

  card:          { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: 24, marginBottom: 80, boxShadow: 'var(--shadow-card)' },
  table:         { width: '100%', borderCollapse: 'collapse' },
  th:            { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--color-muted2)', borderBottom: '1px solid var(--color-border)', textTransform: 'uppercase', letterSpacing: '0.07em' },
  tr:            { borderBottom: '1px solid var(--color-border)' },
  trDirty:       { background: 'rgba(255,87,34,0.04)' },
  td:            { padding: '13px 12px', fontSize: 14, color: 'var(--color-text)', verticalAlign: 'middle' },
  sessionName:   { fontWeight: 500 },
  sessionId:     { fontSize: 11, fontFamily: 'monospace', color: 'var(--color-muted2)', marginTop: 2 },
  statusBadge:   { padding: '3px 9px', borderRadius: 100, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 },
  pulse:         { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: 'scqPulse 1.2s infinite' },
  progressTrack: { height: 5, background: 'var(--color-border)', borderRadius: 3, overflow: 'hidden', width: 100 },
  progressFill:  { height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, var(--color-accent) 0%, var(--color-accent-dim) 50%, var(--color-accent) 100%)', backgroundSize: '200% 100%', animation: 'progressShimmer 1.8s linear infinite' },
  projectSelect:      { fontFamily: 'var(--font-sans)', fontSize: 12, padding: '5px 8px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-card2)', color: 'var(--color-text)', cursor: 'pointer', outline: 'none', maxWidth: 160 },
  projectSelectDirty: { borderColor: 'var(--color-amber)', color: 'var(--color-amber)' },
  tableDropHint: { marginTop: 16, paddingTop: 14, borderTop: '1px dashed var(--color-border)', textAlign: 'center', fontSize: 12, color: 'var(--color-muted2)', cursor: 'pointer' },

  stickyBar:   { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-card)', border: '1px solid var(--color-border2)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 50 },
  stickyMsg:   { fontSize: 13, color: 'var(--color-muted)' },
  deleteBtn:   { background: 'none', border: 'none', color: 'var(--color-muted2)', fontSize: 14, cursor: 'pointer', padding: '2px 6px', borderRadius: 'var(--radius-sm)', lineHeight: 1, opacity: 0.6 },
};
